/**
 * Utility to automatically sync artwork visibility with co-learning spaces
 * When visibility changes to public, artworks are automatically shared to their respective co-learning spaces
 * When visibility changes to private, artworks are removed from co-learning spaces
 */

import { db, auth } from '../firebase';
import { collection, addDoc, doc, updateDoc, deleteDoc, query, where, getDocs, serverTimestamp } from 'firebase/firestore';

/**
 * Determine the activity type and co-learning space from artwork metadata
 * @param {Object} artwork - The artwork object
 * @param {string} collectionName - The collection name (submissions, bookArtworks, etc.)
 * @returns {Object|null} - { activityType: string, coLearningCollection: string } or null
 */
export const getArtworkActivityInfo = (artwork, collectionName = null) => {
  console.log('🔍 [SYNC] Detecting artwork activity info:', {
    collectionName,
    hasActivityId: !!artwork.activityId,
    hasBookArtworkId: !!artwork.bookArtworkId,
    isBookArtwork: artwork.isBookArtwork,
    source: artwork.source,
    tag: artwork.tag,
    hasStyleId: !!artwork.styleId,
    hasStyleName: !!artwork.styleName,
    activityType: artwork.activityType,
    hasArtworkId: !!artwork.artworkId,
    metadata: artwork.metadata
  });

  // Check collection name first (most reliable)
  if (collectionName === 'bookArtworks') {
    console.log('✅ [SYNC] Detected as book co-learning from collection name');
    return {
      activityType: 'book-co-learning',
      coLearningCollection: 'bookArtworks'
    };
  }

  const bookTag =
    artwork.tag ||
    artwork.bookTag ||
    artwork.book ||
    artwork.metadata?.book ||
    '';
  const isKnownBookTag =
    bookTag &&
    (bookTag.includes('Around the world') ||
      bookTag.includes("What's your story") ||
      bookTag.includes('Complete the story') ||
      bookTag.includes('Impressionism') ||
      bookTag.includes('Pointillism'));

  const dailyActivityId = artwork.activityId || artwork.metadata?.activityId;
  const isNumericDailyId =
    dailyActivityId && /^\d+$/.test(String(dailyActivityId).trim());
  const hasBookCountry =
    Boolean(artwork.activityCode || artwork.country || artwork.selectedCountry) &&
    Boolean(artwork.book || artwork.bookTag || isKnownBookTag);

  // Daily art (numeric activityId from Art of the Day) — before book; wins over mistaken isBookArtwork flag
  if (
    isNumericDailyId &&
    !hasBookCountry &&
    !isKnownBookTag
  ) {
    return {
      activityType: 'daily-activity',
      coLearningCollection: 'submissions',
    };
  }

  if (
    collectionName === 'submissions' ||
    artwork.source === 'daily-activity' ||
    artwork.type === 'daily_activity' ||
    artwork.metadata?.type === 'daily_activity'
  ) {
    return {
      activityType: 'daily-activity',
      coLearningCollection: 'submissions',
    };
  }

  // Check for book co-learning artworks — do NOT use bare activityId (shared with daily)
  if (
    artwork.isBookArtwork ||
    artwork.bookArtworkId ||
    artwork.source === 'book-co-learning' ||
    isKnownBookTag ||
    hasBookCountry
  ) {
    console.log('✅ [SYNC] Detected as book co-learning from artwork metadata');
    return {
      activityType: 'book-co-learning',
      coLearningCollection: 'bookArtworks',
    };
  }

  // Check for ways of seeing artworks
  if (artwork.styleId || artwork.styleName || artwork.source === 'ways-of-seeing' || 
      artwork.metadata?.feature === 'ways-of-seeing-journey' ||
      artwork.originalArtworkUrl || artwork.originalArtworkId) {
    console.log('✅ [SYNC] Detected as ways of seeing from artwork metadata');
    return {
      activityType: 'ways-of-seeing',
      coLearningCollection: 'tutorialArtworks'
    };
  }

  // Check for art history artworks
  if (artwork.activityType === 'art-history-appreciation' || artwork.source === 'art-history' || 
      artwork.artworkId || artwork.type === 'art-history-appreciation') {
    console.log('✅ [SYNC] Detected as art history from artwork metadata');
    return {
      activityType: 'art-history',
      coLearningCollection: 'artHistorySharedArtworks' // Correct collection name
    };
  }

  console.log('ℹ️ [SYNC] Artwork is not from an activity - skipping sync');
  return null;
};

/**
 * Share artwork to co-learning space
 * @param {Object} artwork - The artwork object
 * @param {string} coLearningCollection - The collection name (bookArtworks, tutorialArtworks, etc.)
 * @returns {Promise<string>} - The document ID of the shared artwork
 */
export const shareToCoLearningSpace = async (artwork, coLearningCollection) => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('User must be authenticated to share artwork');
  }

  try {
    // Check if artwork already exists in co-learning space
    // Use collection-specific queries for better matching
    let existingDoc = null;
    let existingQuery = null;
    
    if (coLearningCollection === 'artHistorySharedArtworks') {
      // For art history, query by artworkId and uid (most specific)
      const artworkId = artwork.artworkId || artwork.metadata?.artworkMeta?.id || artwork.metadata?.artworkId;
      const imageUrl = artwork.imageUrl || artwork.artworkUrl || artwork.transformedImageUrl;
      
      if (artworkId) {
        existingQuery = query(
          collection(db, coLearningCollection),
          where('artworkId', '==', artworkId),
          where('uid', '==', user.uid)
        );
        console.log('🔍 [SYNC] Checking for existing art history artwork by artworkId:', { artworkId, uid: user.uid });
      } else if (imageUrl) {
        existingQuery = query(
          collection(db, coLearningCollection),
          where('imageUrl', '==', imageUrl),
          where('uid', '==', user.uid)
        );
        console.log('🔍 [SYNC] Checking for existing art history artwork by imageUrl (fallback)');
      }
    } else if (coLearningCollection === 'bookArtworks') {
      // For book artworks, query by book, activityCode, and uid
      const book = artwork.book || artwork.tag;
      const activityCode = artwork.activityCode;
      
      if (book && activityCode) {
        existingQuery = query(
          collection(db, coLearningCollection),
          where('book', '==', book),
          where('activityCode', '==', activityCode),
          where('uid', '==', user.uid)
        );
        console.log('🔍 [SYNC] Checking for existing book artwork by book and activityCode:', { book, activityCode, uid: user.uid });
      } else {
        // Fallback to imageUrl
        existingQuery = query(
          collection(db, coLearningCollection),
          where('imageUrl', '==', artwork.imageUrl || artwork.artworkUrl || artwork.transformedImageUrl),
          where('uid', '==', user.uid)
        );
      }
    } else if (coLearningCollection === 'tutorialArtworks') {
      // For ways of seeing, query by styleId and originalArtworkId (most specific)
      // Note: tutorialArtworks uses 'userId' not 'uid'
      const styleId = artwork.styleId || artwork.metadata?.styleId;
      const originalArtworkId = artwork.originalArtworkId || artwork.metadata?.originalArtworkId;
      const originalArtworkUrl = artwork.originalArtworkUrl || artwork.metadata?.originalImageUrl;
      const imageUrl = artwork.imageUrl || artwork.artworkUrl || artwork.transformedImageUrl;
      
      console.log('🔍 [SYNC] Ways of seeing share - checking fields:', {
        styleId,
        originalArtworkId,
        originalArtworkUrl: originalArtworkUrl ? originalArtworkUrl.substring(0, 50) + '...' : null,
        imageUrl: imageUrl ? imageUrl.substring(0, 50) + '...' : null,
        userId: user.uid,
        hasStyleId: !!styleId,
        hasOriginalArtworkId: !!originalArtworkId
      });
      
      if (styleId && originalArtworkId) {
        // Try styleId + originalArtworkId first (most specific)
        existingQuery = query(
          collection(db, coLearningCollection),
          where('styleId', '==', styleId),
          where('originalArtworkId', '==', originalArtworkId),
          where('userId', '==', user.uid) // tutorialArtworks uses 'userId'
        );
        console.log('🔍 [SYNC] Checking for existing tutorial artwork by styleId and originalArtworkId:', { styleId, originalArtworkId, userId: user.uid });
      } else if (originalArtworkUrl) {
        // Fallback to originalArtworkUrl query
        existingQuery = query(
          collection(db, coLearningCollection),
          where('originalArtworkUrl', '==', originalArtworkUrl),
          where('userId', '==', user.uid) // tutorialArtworks uses 'userId'
        );
        console.log('🔍 [SYNC] Checking for existing tutorial artwork by originalArtworkUrl (fallback):', { 
          originalArtworkUrl: originalArtworkUrl.substring(0, 50) + '...', 
          userId: user.uid 
        });
      } else if (imageUrl) {
        // Last fallback: imageUrl query
        existingQuery = query(
          collection(db, coLearningCollection),
          where('imageUrl', '==', imageUrl),
          where('userId', '==', user.uid) // tutorialArtworks uses 'userId'
        );
        console.log('🔍 [SYNC] Checking for existing tutorial artwork by imageUrl (last fallback)');
      }
    } else {
      // Default: query by imageUrl
      existingQuery = query(
        collection(db, coLearningCollection),
        where('imageUrl', '==', artwork.imageUrl || artwork.transformedImageUrl || artwork.artworkUrl),
        where('uid', '==', user.uid)
      );
    }
    
    if (existingQuery) {
      const existingSnapshot = await getDocs(existingQuery);
      if (!existingSnapshot.empty) {
        existingDoc = existingSnapshot.docs[0];
        console.log(`✅ [SYNC] Found existing artwork in ${coLearningCollection}:`, existingDoc.id);
      }
    }

    // Build artwork data based on collection type
    // Note: tutorialArtworks uses 'userId' not 'uid', other collections use 'uid'
    // CRITICAL: Ensure imageUrl is captured from multiple possible fields
    const imageUrlValue = artwork.imageUrl || artwork.artworkUrl || artwork.transformedImageUrl || '';
    
    let artworkData = {
      imageUrl: imageUrlValue, // Explicitly set imageUrl
      visibility: 'public',
      updatedAt: serverTimestamp()
    };
    
    // Add uid/userId based on collection type
    if (coLearningCollection === 'tutorialArtworks') {
      artworkData.userId = user.uid; // tutorialArtworks uses 'userId'
    } else {
      artworkData.uid = user.uid; // Other collections use 'uid'
    }

    // Collection-specific fields
    if (coLearningCollection === 'artHistorySharedArtworks') {
      const artworkId = artwork.artworkId || artwork.metadata?.artworkMeta?.id || artwork.metadata?.artworkId;
      const artworkTitle = artwork.title || artwork.artworkTitle || artwork.metadata?.artworkMeta?.title || 'Unknown Artwork';
      const artworkArtist = artwork.metadata?.artworkMeta?.artist || artwork.originalArtworkMeta?.artist || 'Unknown Artist';
      const artworkMovement = artwork.metadata?.artworkMeta?.movement || artwork.originalArtworkMeta?.movement || '';
      const artworkImageUrl = artwork.artworkImageUrl || artwork.originalArtworkMeta?.imageUrl || artwork.metadata?.artworkMeta?.imageUrl || '';
      
      artworkData = {
        ...artworkData,
        userName: user.displayName || user.email || 'Anonymous',
        activityType: artwork.activityType || 'art-history-appreciation',
        artworkId: artworkId,
        artworkImageUrl: artworkImageUrl,
        artworkMeta: {
          title: artworkTitle,
          artist: artworkArtist,
          movement: artworkMovement,
          imageUrl: artworkImageUrl
        },
        createdAt: existingDoc ? existingDoc.data().createdAt : serverTimestamp()
      };
      console.log('📝 [SYNC] Art history artwork data:', {
        artworkId,
        artworkTitle,
        hasArtworkMeta: !!artworkData.artworkMeta
      });
    } else if (coLearningCollection === 'bookArtworks') {
      artworkData = {
        ...artworkData,
        title: artwork.title || `Artwork from ${artwork.tag || artwork.book} - Activity ${artwork.activityCode}`,
        description: artwork.description || '',
        technique: artwork.technique || '',
        inspiration: artwork.inspiration || '',
        book: artwork.book || artwork.tag,
        activityCode: artwork.activityCode,
        likesCount: existingDoc ? (existingDoc.data().likesCount || 0) : 0,
        applauseCount: existingDoc ? (existingDoc.data().applauseCount || 0) : 0,
        createdAt: existingDoc ? existingDoc.data().createdAt : serverTimestamp()
      };
    } else if (coLearningCollection === 'tutorialArtworks') {
      // CRITICAL: Ensure imageUrl is explicitly set - this is the main image displayed in the co-learning space
      // The imageUrl should be the transformed/result image, not the original
      const finalImageUrl = imageUrlValue || artwork.imageUrl || artwork.artworkUrl || artwork.transformedImageUrl || '';
      const originalArtworkUrl = artwork.originalArtworkUrl || artwork.originalImageUrl || artwork.metadata?.originalImageUrl || '';
      
      artworkData = {
        userId: user.uid, // tutorialArtworks uses 'userId' (not 'uid')
        imageUrl: finalImageUrl, // CRITICAL: This is the transformed image that displays in the co-learning space
        title: artwork.title || artwork.styleName || 'My Artwork',
        description: artwork.description || '',
        technique: artwork.technique || '',
        inspiration: artwork.inspiration || '',
        styleId: artwork.styleId || artwork.metadata?.styleId,
        styleName: artwork.styleName || artwork.metadata?.styleName,
        originalArtworkId: artwork.originalArtworkId || artwork.metadata?.originalArtworkId,
        originalArtworkUrl: originalArtworkUrl, // The original artwork before transformation
        type: 'tutorial_creation',
        visibility: 'public',
        createdAt: existingDoc ? existingDoc.data().createdAt : serverTimestamp(),
        updatedAt: serverTimestamp()
      };
      
      // Log for debugging - check if imageUrl is missing
      if (!finalImageUrl) {
        console.warn('⚠️ [SYNC] Ways of seeing artwork missing imageUrl:', {
          styleId: artworkData.styleId,
          styleName: artworkData.styleName,
          hasArtworkImageUrl: !!artwork.imageUrl,
          hasArtworkArtworkUrl: !!artwork.artworkUrl,
          hasArtworkTransformedImageUrl: !!artwork.transformedImageUrl,
          artworkKeys: Object.keys(artwork)
        });
      } else {
        console.log('✅ [SYNC] Ways of seeing artwork data for tutorialArtworks:', {
          styleId: artworkData.styleId,
          styleName: artworkData.styleName,
          hasImageUrl: !!artworkData.imageUrl,
          imageUrlPreview: artworkData.imageUrl.substring(0, 80) + '...',
          hasOriginalArtworkUrl: !!artworkData.originalArtworkUrl
        });
      }
    } else {
      // Default/general artwork
      artworkData = {
        ...artworkData,
        title: artwork.title || 'My Artwork',
        description: artwork.description || '',
        createdAt: existingDoc ? existingDoc.data().createdAt : serverTimestamp()
      };
    }

    if (existingDoc) {
      // Update existing document
      await updateDoc(doc(db, coLearningCollection, existingDoc.id), {
        ...artworkData,
        visibility: 'public',
        updatedAt: serverTimestamp()
      });
      console.log(`✅ Updated artwork in ${coLearningCollection}:`, existingDoc.id);
      return existingDoc.id;
    } else {
      // Create new document
      const docRef = await addDoc(collection(db, coLearningCollection), artworkData);
      console.log(`✅ Shared artwork to ${coLearningCollection}:`, docRef.id);
      return docRef.id;
    }
  } catch (error) {
    console.error(`❌ Error sharing to ${coLearningCollection}:`, error);
    throw error;
  }
};

/**
 * Remove artwork from co-learning space
 * @param {Object} artwork - The artwork object
 * @param {string} coLearningCollection - The collection name
 */
export const removeFromCoLearningSpace = async (artwork, coLearningCollection) => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('User must be authenticated to remove artwork');
  }

  try {
    let artworkQuery;
    
    // Build query based on collection type
    if (coLearningCollection === 'bookArtworks') {
      // For book artworks, query by book, activityCode, and uid
      const book = artwork.book || artwork.tag;
      const activityCode = artwork.activityCode;
      
      // Check if book and activityCode are valid (not null, undefined, or empty string)
      const hasBook = book && book.trim() !== '';
      const hasActivityCode = activityCode && activityCode.toString().trim() !== '';
      
      if (hasBook && hasActivityCode) {
        // Use book + activityCode query (most specific)
        artworkQuery = query(
          collection(db, coLearningCollection),
          where('book', '==', book),
          where('activityCode', '==', activityCode),
          where('uid', '==', user.uid)
        );
        console.log('🔍 [SYNC] Querying bookArtworks by book and activityCode:', { book, activityCode, uid: user.uid });
      } else {
        // Fallback to imageUrl query (works even if activityCode is missing)
        console.log('ℹ️ [SYNC] Using fallback query for bookArtworks (missing book or activityCode):', { book, activityCode, hasBook, hasActivityCode });
        artworkQuery = query(
          collection(db, coLearningCollection),
          where('imageUrl', '==', artwork.imageUrl || artwork.artworkUrl || artwork.transformedImageUrl),
          where('uid', '==', user.uid)
        );
      }
    } else if (coLearningCollection === 'tutorialArtworks') {
      // For ways of seeing, query by styleId and originalArtworkId
      // Note: tutorialArtworks uses 'userId' not 'uid'
      const styleId = artwork.styleId || artwork.metadata?.styleId;
      const originalArtworkId = artwork.originalArtworkId || artwork.metadata?.originalArtworkId;
      const originalArtworkUrl = artwork.originalArtworkUrl || artwork.metadata?.originalImageUrl;
      
      console.log('🔍 [SYNC] Ways of seeing removal - checking fields:', {
        styleId,
        originalArtworkId,
        originalArtworkUrl: originalArtworkUrl ? originalArtworkUrl.substring(0, 50) + '...' : null,
        userId: user.uid,
        hasStyleId: !!styleId,
        hasOriginalArtworkId: !!originalArtworkId,
        artworkKeys: Object.keys(artwork)
      });
      
      if (styleId && originalArtworkId) {
        // Try styleId + originalArtworkId first (most specific)
        artworkQuery = query(
          collection(db, coLearningCollection),
          where('styleId', '==', styleId),
          where('originalArtworkId', '==', originalArtworkId),
          where('userId', '==', user.uid) // tutorialArtworks uses 'userId'
        );
        console.log('🔍 [SYNC] Querying tutorialArtworks by styleId and originalArtworkId:', { styleId, originalArtworkId, userId: user.uid });
      } else if (originalArtworkUrl) {
        // Fallback to originalArtworkUrl query
        artworkQuery = query(
          collection(db, coLearningCollection),
          where('originalArtworkUrl', '==', originalArtworkUrl),
          where('userId', '==', user.uid) // tutorialArtworks uses 'userId'
        );
        console.log('🔍 [SYNC] Querying tutorialArtworks by originalArtworkUrl (fallback):', { 
          originalArtworkUrl: originalArtworkUrl.substring(0, 50) + '...', 
          userId: user.uid 
        });
      } else {
        // Last fallback: imageUrl query
        artworkQuery = query(
          collection(db, coLearningCollection),
          where('imageUrl', '==', artwork.imageUrl || artwork.artworkUrl || artwork.transformedImageUrl),
          where('userId', '==', user.uid) // tutorialArtworks uses 'userId'
        );
        console.log('🔍 [SYNC] Querying tutorialArtworks by imageUrl (last fallback)');
      }
    } else if (coLearningCollection === 'artHistorySharedArtworks') {
      // For art history, query by artworkId and uid
      // Try multiple possible locations for artworkId
      const artworkId = artwork.artworkId || 
                       artwork.metadata?.artworkMeta?.id || 
                       artwork.metadata?.artworkId;
      
      const imageUrl = artwork.imageUrl || artwork.artworkUrl || artwork.transformedImageUrl;
      
      console.log('🔍 [SYNC] Art history removal - checking fields:', {
        artworkId,
        imageUrl: imageUrl ? imageUrl.substring(0, 50) + '...' : null,
        uid: user.uid,
        hasArtworkId: !!artworkId,
        artworkKeys: Object.keys(artwork)
      });
      
      if (artworkId) {
        // Try artworkId first (most specific)
        artworkQuery = query(
          collection(db, coLearningCollection),
          where('artworkId', '==', artworkId),
          where('uid', '==', user.uid)
        );
        console.log('🔍 [SYNC] Querying artHistorySharedArtworks by artworkId:', { artworkId, uid: user.uid });
      } else if (imageUrl) {
        // Fallback to imageUrl query (more reliable if artworkId is missing)
        artworkQuery = query(
          collection(db, coLearningCollection),
          where('imageUrl', '==', imageUrl),
          where('uid', '==', user.uid)
        );
        console.log('🔍 [SYNC] Querying artHistorySharedArtworks by imageUrl (fallback):', { 
          imageUrl: imageUrl.substring(0, 50) + '...', 
          uid: user.uid 
        });
      } else {
        throw new Error('Cannot remove art history artwork: missing both artworkId and imageUrl');
      }
    } else {
      // Default: query by imageUrl
      artworkQuery = query(
        collection(db, coLearningCollection),
        where('imageUrl', '==', artwork.imageUrl || artwork.artworkUrl || artwork.transformedImageUrl),
        where('uid', '==', user.uid)
      );
      console.log('🔍 [SYNC] Querying by imageUrl (default)');
    }
    
    const snapshot = await getDocs(artworkQuery);

    if (!snapshot.empty) {
      // Update visibility to private instead of deleting (preserves history)
      for (const docSnap of snapshot.docs) {
        await updateDoc(doc(db, coLearningCollection, docSnap.id), {
          visibility: 'private',
          updatedAt: serverTimestamp()
        });
        console.log(`✅ [SYNC] Removed artwork from ${coLearningCollection}:`, docSnap.id);
      }
      return snapshot.docs.length;
    } else {
      console.log(`ℹ️ [SYNC] Artwork not found in ${coLearningCollection}, nothing to remove`);
      return 0;
    }
  } catch (error) {
    console.error(`❌ [SYNC] Error removing from ${coLearningCollection}:`, error);
    throw error;
  }
};

/**
 * Sync artwork visibility with co-learning spaces
 * This is the main function to call when visibility changes
 * @param {Object} artwork - The artwork object
 * @param {string} newVisibility - 'public' or 'private' (or true/false for isPublic)
 * @param {string} oldVisibility - Previous visibility (optional)
 * @param {string} collectionName - The collection name where the artwork is stored
 */
export const syncArtworkVisibility = async (artwork, newVisibility, oldVisibility = null, collectionName = null) => {
  // Normalize visibility (handle both 'public'/'private' and true/false)
  const normalizedNew = typeof newVisibility === 'boolean' ? (newVisibility ? 'public' : 'private') : newVisibility;
  let normalizedOld = oldVisibility !== null 
    ? (typeof oldVisibility === 'boolean' ? (oldVisibility ? 'public' : 'private') : oldVisibility)
    : null;

  // Get activity info
  const activityInfo = getArtworkActivityInfo(artwork, collectionName);
  if (!activityInfo) {
    console.log('ℹ️ [SYNC] Artwork is not from an activity (book co-learning, ways of seeing, or art history), skipping sync');
    return;
  }

  // CRITICAL: For ways of seeing, only fetch the actual visibility if oldVisibility was not provided
  // If oldVisibility is provided, it was fetched BEFORE the update, so use it directly
  // This prevents the issue where we check AFTER the update and see no change
  if (activityInfo.activityType === 'ways-of-seeing' && normalizedOld === null) {
    try {
      const imageUrl = artwork.imageUrl || artwork.artworkUrl || artwork.transformedImageUrl;
      const styleId = artwork.styleId || artwork.metadata?.styleId;
      const originalArtworkId = artwork.originalArtworkId || artwork.metadata?.originalArtworkId;
      const originalArtworkUrl = artwork.originalArtworkUrl || artwork.metadata?.originalImageUrl;
      
      let tutorialQuery = null;
      if (styleId && originalArtworkId) {
        tutorialQuery = query(
          collection(db, activityInfo.coLearningCollection),
          where('styleId', '==', styleId),
          where('originalArtworkId', '==', originalArtworkId),
          where('userId', '==', auth.currentUser.uid)
        );
      } else if (originalArtworkUrl) {
        tutorialQuery = query(
          collection(db, activityInfo.coLearningCollection),
          where('originalArtworkUrl', '==', originalArtworkUrl),
          where('userId', '==', auth.currentUser.uid)
        );
      } else if (imageUrl) {
        tutorialQuery = query(
          collection(db, activityInfo.coLearningCollection),
          where('imageUrl', '==', imageUrl),
          where('userId', '==', auth.currentUser.uid)
        );
      }
      
      if (tutorialQuery) {
        const snapshot = await getDocs(tutorialQuery);
        if (!snapshot.empty) {
          normalizedOld = snapshot.docs[0].data().visibility;
          console.log('📋 [SYNC] Found in tutorialArtworks, using visibility from there:', normalizedOld);
        } else {
          normalizedOld = 'private'; // Not found in co-learning space, so it's effectively private
          console.log('📋 [SYNC] Not found in tutorialArtworks, assuming old visibility: private');
        }
      }
    } catch (error) {
      console.warn('⚠️ [SYNC] Error fetching old visibility from tutorialArtworks, falling back:', error);
      // Fallback to 'private' if we can't determine
      if (normalizedOld === null) {
        normalizedOld = 'private';
      }
    }
  } else if (activityInfo.activityType === 'ways-of-seeing' && normalizedOld !== null) {
    console.log('📋 [SYNC] Using provided oldVisibility for ways of seeing (fetched before update):', normalizedOld);
  }

  // Only sync if visibility actually changed
  if (normalizedOld === normalizedNew) {
    console.log('ℹ️ [SYNC] Visibility unchanged, skipping sync');
    return;
  }

  try {
    if (normalizedNew === 'public') {
      // Share to co-learning space
      await shareToCoLearningSpace(artwork, activityInfo.coLearningCollection);
      console.log(`✅ Artwork automatically shared to ${activityInfo.activityType} co-learning space`);
    } else if (normalizedNew === 'private') {
      // Remove from co-learning space
      await removeFromCoLearningSpace(artwork, activityInfo.coLearningCollection);
      console.log(`✅ Artwork automatically removed from ${activityInfo.activityType} co-learning space`);
    }
  } catch (error) {
    console.error('❌ Error syncing artwork visibility:', error);
    // Don't throw - allow visibility update to proceed even if sync fails
  }
};

